// src/hooks/useResizableWidth.ts
// A user-draggable pixel width, persisted both locally (instant, and works
// offline) and per-account server-side (so it follows the doctor across
// devices/browsers, not just one browser's localStorage) — see
// backend/routes/ui-preferences.js. Used by the v3 Consultation layout's
// resizable rail and assistant-column widths; written generically so a
// future resizable panel elsewhere in the app can reuse it rather than
// growing its own copy of this drag math.
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { getUiPreferences, patchUiPreferences } from '@/api';

// One GET for the whole session, shared by every useResizableWidth instance
// mounted at once (the Consultation screen has two) — there's no reason for
// each to fetch the same small preferences blob separately.
let serverPreferencesOnce: Promise<Record<string, unknown>> | null = null;
function loadServerPreferences(): Promise<Record<string, unknown>> {
  if (!serverPreferencesOnce) serverPreferencesOnce = getUiPreferences().catch(() => ({}));
  return serverPreferencesOnce;
}

export interface ResizableWidthOptions {
  /** localStorage key and the preference's key in the server-side JSON blob — the same string serves both. */
  key: string;
  defaultValue: number;
  min: number;
  max: number;
}

export function useResizableWidth({ key, defaultValue, min, max }: ResizableWidthOptions): {
  width: number;
  /** Wire to a drag handle's onMouseDown. */
  startResize: (e: ReactMouseEvent<HTMLDivElement>) => void;
} {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const [width, setWidth] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem(key));
      return saved >= min && saved <= max ? saved : defaultValue;
    } catch { return defaultValue; }
  });
  const resizingRef = useRef(false);

  // Reconciles with the account-level value once per mount — it wins over
  // whatever this particular browser's localStorage had (e.g. the first
  // visit from a new device), but only if the server actually has a saved
  // value for this key; otherwise the localStorage/default seed above
  // stands.
  useEffect(() => {
    let cancelled = false;
    loadServerPreferences().then((prefs) => {
      if (cancelled) return;
      const fromServer = Number(prefs[key]);
      if (fromServer >= min && fromServer <= max) {
        setWidth(fromServer);
        try { localStorage.setItem(key, String(fromServer)); } catch { /* private browsing — server copy still applies this session */ }
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const persist = (value: number) => {
    try { localStorage.setItem(key, String(value)); } catch { /* private browsing — the in-memory value still works this session */ }
    // Best-effort: a failed save just means next session starts from the
    // last value the server does have (or the default) — never blocks or
    // errors the drag itself.
    patchUiPreferences({ [key]: value }).catch(() => {});
  };

  const startResize = (e: ReactMouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    resizingRef.current = true;
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      setWidth(clamp(startWidth + (ev.clientX - startX)));
    };
    const onUp = (ev: MouseEvent) => {
      resizingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      persist(clamp(startWidth + (ev.clientX - startX)));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
  };

  return { width, startResize };
}

export interface ResizableSplitOptions {
  /** localStorage key and server-side preference key, as for useResizableWidth. */
  key: string;
  /** Default share of the row for each pane; normalised to sum to 1. */
  defaults: number[];
  /** Minimum width in px for each pane (one number for all, or one per pane). */
  minPx: number | number[];
  /**
   * Width of ONE divider. Each pane renders at `(rowWidth + gutterPx) * fraction - gutterPx`
   * (every pane "owns" one gutter), so N panes plus N-1 dividers fill the row
   * exactly — and a 2/5 : 3/5 split lines up with a 5-column grid whose gap
   * equals the divider width.
   */
  gutterPx: number;
}

/**
 * A row of panes separated by draggable dividers, stored as FRACTIONS of the
 * row rather than pixels — for a row whose width follows the window (the
 * Dashboard's card rows), where a saved pixel width would mean something
 * different on the next screen. Dragging divider i moves width between pane
 * i and pane i+1 only, never below either pane's `minPx` (judged against the
 * row's width at drag time). Persisted like useResizableWidth.
 */
export function useResizableSplit({ key, defaults, minPx, gutterPx }: ResizableSplitOptions): {
  fractions: number[];
  /** Wire to divider i's onMouseDown (i = the pane on its left). */
  startResize: (index: number) => (e: ReactMouseEvent<HTMLElement>) => void;
} {
  const n = defaults.length;
  const normalise = (v: unknown): number[] | null => {
    if (!Array.isArray(v) || v.length !== n || !v.every(x => typeof x === 'number' && x > 0 && Number.isFinite(x))) return null;
    const sum = (v as number[]).reduce((a, b) => a + b, 0);
    return (v as number[]).map(x => x / sum);
  };
  const fallback = normalise(defaults) ?? defaults;
  const [fractions, setFractions] = useState<number[]>(() => {
    try { return normalise(JSON.parse(localStorage.getItem(key) ?? 'null')) ?? fallback; } catch { return fallback; }
  });

  useEffect(() => {
    let cancelled = false;
    loadServerPreferences().then((prefs) => {
      if (cancelled) return;
      const fromServer = normalise(prefs[key]);
      if (fromServer) {
        setFractions(fromServer);
        try { localStorage.setItem(key, JSON.stringify(fromServer)); } catch { /* server copy still applies this session */ }
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const startResize = (index: number) => (e: ReactMouseEvent<HTMLElement>) => {
    const row = e.currentTarget.parentElement;
    if (!row || index < 0 || index >= n - 1) return;
    e.preventDefault();
    const usable = Math.max(1, row.getBoundingClientRect().width + gutterPx);
    const mins = Array.isArray(minPx) ? minPx : Array(n).fill(minPx);
    const start = fractions;
    const startX = e.clientX;
    const pair = start[index]! + start[index + 1]!;
    const minLeft = ((mins[index] ?? 0) + gutterPx) / usable;
    const minRight = ((mins[index + 1] ?? 0) + gutterPx) / usable;
    const compute = (clientX: number) => {
      // If the row is too narrow for both minimums, keep the split as it was.
      if (minLeft + minRight > pair) return start;
      const left = Math.min(pair - minRight, Math.max(minLeft, start[index]! + (clientX - startX) / usable));
      const next = [...start];
      next[index] = left;
      next[index + 1] = pair - left;
      return next;
    };
    const onMove = (ev: MouseEvent) => setFractions(compute(ev.clientX));
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      const final = compute(ev.clientX);
      setFractions(final);
      try { localStorage.setItem(key, JSON.stringify(final)); } catch { /* in-memory value still works */ }
      patchUiPreferences({ [key]: final }).catch(() => {});
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
  };

  return { fractions, startResize };
}
